/**
 * Dashboard KPI cards (W1-ADMIN, D358) — the pure decision of what each dashboard number shows. The rule is "real data
 * or an explicit unavailable": a KPI the server names in `unavailable`, a rate over zero requested rides, or money that
 * is not a real amount in an assigned currency (including the ISO 4217 test/no-currency codes a read model uses as a
 * neutral placeholder) is `unavailable`, never a fabricated `0`.
 */
import { isIso4217Currency } from '@vello/core';
import { type OpsDashboardView } from '@vello/api-client';

export type DashboardKpiId = 'requested' | 'completed' | 'cancelled' | 'completionRate' | 'gmv' | 'takeRate';

export type DashboardKpiCard = { readonly id: DashboardKpiId } & (
  | { readonly kind: 'loading' }
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'count'; readonly value: number }
  | { readonly kind: 'ratio'; readonly value: number }
  | { readonly kind: 'money'; readonly amountMinor: bigint; readonly currency: string }
);

const ORDER: readonly DashboardKpiId[] = ['requested', 'completed', 'cancelled', 'completionRate', 'gmv', 'takeRate'];
/** ISO 4217 codes that are assigned but never a real amount of money (testing / no currency). */
const PLACEHOLDER_CURRENCIES = new Set(['XTS', 'XXX']);

function minor(raw: string): bigint | undefined {
  return /^-?\d+$/.test(raw) ? BigInt(raw) : undefined;
}

/** The six dashboard cards for a loaded view (or all `loading` before the first read returns). */
export function dashboardKpiCards(view: OpsDashboardView | undefined): readonly DashboardKpiCard[] {
  if (view === undefined) return ORDER.map((id) => ({ id, kind: 'loading' as const }));
  const k = view.kpis;
  const missing = new Set(view.unavailable ?? []);
  const count = (id: DashboardKpiId, field: 'requested' | 'completed' | 'cancelled'): DashboardKpiCard =>
    missing.has(field) || !Number.isInteger(k[field]) || k[field] < 0 ? { id, kind: 'unavailable' } : { id, kind: 'count', value: k[field] };

  const completionRate: DashboardKpiCard =
    missing.has('completionRate') || missing.has('requested') || k.requested <= 0 || !Number.isFinite(k.completionRate)
      ? { id: 'completionRate', kind: 'unavailable' }
      : { id: 'completionRate', kind: 'ratio', value: k.completionRate };

  const gmvMinor = minor(k.gmv.amountMinor);
  const gmv: DashboardKpiCard =
    missing.has('gmv') || gmvMinor === undefined || gmvMinor < 0n || PLACEHOLDER_CURRENCIES.has(k.gmv.currency) || !isIso4217Currency(k.gmv.currency)
      ? { id: 'gmv', kind: 'unavailable' }
      : { id: 'gmv', kind: 'money', amountMinor: gmvMinor, currency: k.gmv.currency };

  const takeRate: DashboardKpiCard =
    missing.has('takeRateBps') || missing.has('netRevenue') || missing.has('gmv') || !Number.isFinite(k.takeRateBps)
      ? { id: 'takeRate', kind: 'unavailable' }
      : { id: 'takeRate', kind: 'ratio', value: k.takeRateBps / 10_000 };

  return [count('requested', 'requested'), count('completed', 'completed'), count('cancelled', 'cancelled'), completionRate, gmv, takeRate];
}
